import React from 'react';
import { motion } from 'framer-motion';
import { Button } from 'components/ui';
import { Trans } from 'react-i18next';
import { HiPlus, HiOutlinePhotograph } from 'react-icons/hi';
import { BsImage, BsPalette, BsMagic } from 'react-icons/bs';

/**
 * EmptyState Component for AI Image Generation
 * 
 * Shows when user has no generated images yet
 * 
 * @param {Object} props
 * @param {Function} props.onCreateClick - Callback for starting image creation
 */
const EmptyState = ({ onCreateClick }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 px-6"
        >
            {/* Icon */}
            <motion.div
                animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                }}
                transition={{ duration: 4, repeat: Infinity }}
                className="w-32 h-32 rounded-3xl bg-gradient-to-br from-pink-500/20 to-orange-500/20 flex items-center justify-center backdrop-blur-sm border border-pink-500/30 mb-8 relative"
            >
                <HiOutlinePhotograph className="text-6xl text-pink-600 dark:text-pink-400" />
                
                {/* Floating decorative elements */}
                <motion.div
                    animate={{ 
                        y: [0, -5, 0],
                        opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-orange-400 rounded-full flex items-center justify-center"
                >
                    <BsPalette className="text-white text-sm" />
                </motion.div>
                
                <motion.div
                    animate={{ 
                        y: [0, 5, 0],
                        opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute -bottom-2 -left-2 w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center"
                >
                    <BsMagic className="text-white text-sm" />
                </motion.div>
            </motion.div>
            
            {/* Content */}
            <div className="text-center max-w-md mb-8">
                <h2 className="!text-3xl font-black text-gray-900 dark:text-white mb-3">
                    <Trans i18nKey="aiImage.noImagesYet">No Images Generated Yet</Trans>
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed">
                    <Trans i18nKey="aiImage.noImagesYetDescription">
                        Start creating stunning AI-generated images for your social media. Just describe what you want, and let AI bring your vision to life.
                    </Trans>
                </p>
            </div>
            
            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mb-8">
                {[
                    { 
                        step: '1', 
                        title: <Trans i18nKey="aiImage.emptyStep1Title">Describe Your Image</Trans>, 
                        desc: <Trans i18nKey="aiImage.emptyStep1Desc">Write a prompt describing what you want to see</Trans>
                    },
                    { 
                        step: '2', 
                        title: <Trans i18nKey="aiImage.emptyStep2Title">Choose Style & Size</Trans>, 
                        desc: <Trans i18nKey="aiImage.emptyStep2Desc">Select your preferred model and image dimensions</Trans>
                    },
                    { 
                        step: '3', 
                        title: <Trans i18nKey="aiImage.emptyStep3Title">Generate & Download</Trans>, 
                        desc: <Trans i18nKey="aiImage.emptyStep3Desc">Create your image and use it in your posts</Trans>
                    }
                ].map((item) => (
                    <motion.div
                        key={item.step}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: parseFloat(item.step) * 0.1 }}
                        className="bg-white dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-white/10 flex flex-col justify-center items-center text-center gap-4"
                    >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/20 to-orange-500/20 text-pink-600 dark:text-pink-400 font-bold flex items-center justify-center text-lg">
                            {item.step}
                        </div>
                        <div className='flex flex-col flex-1'>
                            <h3 className="!text-lg text-gray-900 dark:text-white font-bold mb-1">{item.title}</h3>
                            <p className="!text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
            
            {/* CTA Button */}
            <Button
                variant="solid"
                size="lg"
                className="!bg-gradient-to-r !from-pink-500 !to-orange-500 hover:!from-pink-600 hover:!to-orange-600 !shadow-2xl !shadow-pink-500/50"
                icon={<HiPlus />}
                onClick={onCreateClick}
            >
                <Trans i18nKey="aiImage.createYourFirstImage">Create Your First Image</Trans>
            </Button>
        </motion.div>
    );
};

export default EmptyState;

